import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, Globe, Award, Target, CheckCircle, XCircle, ArrowRight, AlertTriangle } from 'lucide-react';

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">About Us</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">About IMCAC</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-3xl">
            Global Competency Examination & Certification Board for Post-Licensure Healthcare Professionals
          </p>
        </div>
      </section>

      {/* Disclaimer Banner */}
      <section className="bg-[#F8FAFC] border-b border-slate-200 py-5">
        <div className="imcac-container">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600 leading-relaxed">
              IMCAC is an independent competency assessment and certification body. IMCAC does not provide training, workshops, academic programs, or medical licenses. Certification does not replace professional registration requirements imposed by national regulatory authorities.
            </p>
          </div>
        </div>
      </section>

      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <h2 className="text-3xl font-semibold text-[#1B2A4A] font-heading mb-6">Our Mission</h2>
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                To establish globally benchmarked competency standards for post-licensure healthcare professionals through independent assessment and certification.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                IMCAC assesses knowledge, clinical reasoning, practical competence, professional judgment, and ethical practice through structured competency-based examinations.
              </p>

              <h3 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-4">What We Do</h3>
              <div className="space-y-3 mb-8">
                {[
                  'Competency Assessment — Rigorous evaluation of clinical knowledge and practical skills',
                  'Professional Certification — Board-level certification for qualified healthcare professionals',
                  'Examination Development — Design and administration of standardised competency-based examinations',
                  'Standards Benchmarking — Alignment with international competency frameworks',
                  'Certification Maintenance — Ongoing verification of continued professional competence',
                  'Continuing Professional Development Recognition — CPD framework for recertification',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <h3 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-4">What We Do Not Do</h3>
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-5 mb-6">
                <div className="space-y-3">
                  {[
                    'Conduct workshops or deliver training programs',
                    'Operate medical schools or educational institutions',
                    'Provide degrees or academic qualifications',
                    'Issue licenses to practice medicine',
                    'Replace government regulatory bodies',
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <XCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-6">
              <img
                src="https://customer-assets.emergentagent.com/job_cbeam-portal/artifacts/owme7yia_DSC_1416.JPG"
                alt="IMCAC"
                className="w-full h-64 object-cover rounded-sm shadow-md"
              />
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Globe, label: 'Examination Board', value: 'Independent' },
                  { icon: Award, label: 'Examiners & Assessors', value: 'International' },
                  { icon: Target, label: 'Competency Assessment', value: 'Standardised' },
                  { icon: Shield, label: 'Certifications', value: 'Post-Licensure' },
                ].map((stat, i) => (
                  <Card key={i} className="imcac-card">
                    <CardContent className="p-4 text-center">
                      <stat.icon className="h-6 w-6 text-[#C5A572] mx-auto mb-2" />
                      <p className="text-lg font-bold text-[#1B2A4A] font-heading">{stat.value}</p>
                      <p className="text-xs text-slate-500">{stat.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container text-center">
          <h2 className="text-3xl font-semibold text-[#1B2A4A] font-heading mb-4">Positioning Statement</h2>
          <p className="text-base text-slate-600 max-w-3xl mx-auto leading-relaxed mb-8">
            IMCAC is an independent competency examination and certification board dedicated to establishing globally benchmarked standards for post-licensure healthcare professionals. IMCAC operates under transparent governance, examination integrity, and professional certification principles.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/examination-pathways" data-testid="about-exams-link">
              <Button className="bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm">
                Examination Pathways <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/contact" data-testid="about-contact-link">
              <Button variant="outline" className="rounded-sm border-[#1B2A4A] text-[#1B2A4A]">Contact Us</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
